import React, { useState, useEffect, useCallback } from "react";
import { Card, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import AdminDashboardLayout from "../../../Components/AdminDashboard/AdminDashboardLayout";
import Loading from "react-fullscreen-loading";
import ShowTime from "../../../utils/ShowTime/ShowTime";
import { FaCopy, FaHistory, FaCoins } from "react-icons/fa";
import { useAuth } from "../../../authentication/AuthContext";
import { CREDIT_PACKS, DAYS_PER_YEAR, profitPerYearLicense, bestMarginPack, money } from "../../../Components/CreditPacks/creditPacks";

const statCardStyle = {
  borderRadius: "10px",
  padding: "24px 20px",
  color: "#fff",
  marginBottom: "16px",
};
const statLabel = { fontSize: "12px", fontWeight: 600, opacity: 0.8, marginBottom: "6px", letterSpacing: "0.5px" };
const statValue = { fontSize: "32px", fontWeight: 700, lineHeight: 1 };

const TX_LIMIT = 20;

// Signup link shared with users; the reseller code is embedded as a query param
// so it is pre-filled for them (e.g. https://app.cadomation.com/signup?reseller=AB3CD5EF).
const buildSignupLink = (code) =>
  `${process.env.REACT_APP_PUBLIC_APP_URL || window.location.origin}/signup?reseller=${encodeURIComponent(code)}`;

export default function ResellerDashboard() {
  const { can } = useAuth();
  const [screenloader, setScreenloader] = useState(true);
  const [profile, setProfile] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);

  const fetchDashboard = useCallback(() => {
    const headers = { "access-token": localStorage.getItem("accessToken") };
    const base = process.env.REACT_APP_API_URL;

    axios
      .get(`${base}/reseller/dashboard`, { headers, params: { page: txPage, limit: TX_LIMIT } })
      .then((res) => {
        setScreenloader(false);
        if (res.data.status) {
          setProfile(res.data.profile);
          setTransactions(res.data.transactions || []);
          setTxTotal(res.data.meta?.pagination?.total || 0);
        } else {
          toast.error(res.data.message || "Could not load dashboard");
        }
      })
      .catch(() => {
        setScreenloader(false);
        toast.error("Could not load dashboard data");
      });
  }, [txPage]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const totalCredits = profile?.total_credits ?? 0;
  const availableCredits = profile?.available_credits ?? 0;
  const usedCredits = totalCredits - availableCredits;

  const totalPages = Math.ceil(txTotal / TX_LIMIT);

  const pagerBtnStyle = (disabled) => ({
    padding: "4px 14px",
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    backgroundColor: disabled ? "#f9fafb" : "#fff",
    color: disabled ? "#9ca3af" : "#1e293b",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: "13px",
    fontWeight: 600,
  });

  return (
    <div>
      <AdminDashboardLayout />
      <Loading loading={screenloader} background="#fff" loaderColor="#883484" />
      <div className="content-page-bg">
        <div className="content-page">
          <div className="content">

            <h2>My Dashboard</h2>
            <p className="text-muted">Credit overview and transaction history.</p>

            {/* Reseller code chip */}
            {profile?.reseller_code && (
              <div className="mb-4">
                <Card>
                  <Card.Body className="d-flex flex-wrap align-items-center gap-3 py-3">
                    <span className="text-muted" style={{ fontSize: "13px", fontWeight: 600 }}>Your Reseller Code:</span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        cursor: "pointer",
                        backgroundColor: "#e2e7f1",
                        padding: "4px 16px",
                        borderRadius: "8px",
                        transition: "background-color 0.2s",
                      }}
                      onClick={() => {
                        navigator.clipboard.writeText(profile.reseller_code);
                        toast.success("Reseller Code copied!");
                      }}
                      title="Click to copy"
                    >
                      <span
                        style={{
                          fontFamily: "monospace",
                          fontSize: "22px",
                          fontWeight: 700,
                          letterSpacing: "4px",
                          color: "#1e293b",
                        }}
                      >
                        {profile.reseller_code}
                      </span>
                      <FaCopy style={{ color: "#94a3b8", fontSize: "16px" }} />
                    </div>
                    <span className="text-muted" style={{ fontSize: "12px" }}>
                      Share with users at signup to associate them with you.
                    </span>

                    <div style={{ flexBasis: "100%" }} />

                    <span className="text-muted" style={{ fontSize: "13px", fontWeight: 600 }}>Your Signup Link:</span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        cursor: "pointer",
                        backgroundColor: "#e2e7f1",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        maxWidth: "100%",
                      }}
                      onClick={() => {
                        navigator.clipboard.writeText(buildSignupLink(profile.reseller_code));
                        toast.success("Signup link copied!");
                      }}
                      title="Click to copy"
                    >
                      <span
                        style={{
                          fontFamily: "monospace",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#1e293b",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {buildSignupLink(profile.reseller_code)}
                      </span>
                      <FaCopy style={{ color: "#94a3b8", fontSize: "16px", flexShrink: 0 }} />
                    </div>
                    <span className="text-muted" style={{ fontSize: "12px" }}>
                      Users who sign up through this link are linked to you automatically.
                    </span>
                  </Card.Body>
                </Card>
              </div>
            )}

            {/* Credit stat cards */}
            <div className="row mb-4">
              <div className="col-md-4">
                <div style={{ ...statCardStyle, backgroundColor: "#1e293b" }}>
                  <div style={statLabel}>TOTAL CREDITS ASSIGNED</div>
                  <div style={statValue}>{totalCredits.toLocaleString()}</div>
                  <div style={{ fontSize: "14px", marginTop: "6px", opacity: 0.7 }}>1 credit = 1 day of license</div>
                </div>
              </div>
              <div className="col-md-4">
                <div style={{ ...statCardStyle, backgroundColor: "#016641" }}>
                  <div style={statLabel}>AVAILABLE CREDITS</div>
                  <div style={statValue}>{availableCredits.toLocaleString()}</div>
                  <div style={{ fontSize: "14px", marginTop: "6px", opacity: 0.7 }}>
                    ≈ {Math.floor(availableCredits / 365)} yr {availableCredits % 365} days remaining
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div style={{ ...statCardStyle, backgroundColor: "#523ba8" }}>
                  <div style={statLabel}>CREDITS USED</div>
                  <div style={statValue}>{usedCredits.toLocaleString()}</div>
                  <div style={{ fontSize: "14px", marginTop: "6px", opacity: 0.7 }}>
                    {txTotal} transaction{txTotal !== 1 ? "s" : ""} total
                  </div>
                </div>
              </div>
            </div>

            {/* Buy credits — the upsell. Leads with the shortfall when the balance
                can no longer cover a one-year license, since that is the moment
                a reseller with a customer waiting actually needs it. */}
            {profile && can("reseller.credits.request") && (
              <div
                className="mb-4 d-flex flex-wrap align-items-center justify-content-between gap-3"
                style={{
                  borderRadius: "12px",
                  padding: "18px 22px",
                  background: availableCredits < DAYS_PER_YEAR ? "#fffbeb" : "#f0fdf4",
                  border: `1px solid ${availableCredits < DAYS_PER_YEAR ? "#fde68a" : "#bbf7d0"}`,
                }}
              >
                <div className="d-flex align-items-start gap-3">
                  <FaCoins style={{ fontSize: "24px", marginTop: "2px", color: availableCredits < DAYS_PER_YEAR ? "#b45309" : "#16a34a" }} />
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>
                      {availableCredits < DAYS_PER_YEAR
                        ? "Not enough credits for a 1-year license"
                        : `Earn up to ${money(profitPerYearLicense(bestMarginPack()))} on every license you sell`}
                    </div>
                    <div className="text-muted" style={{ fontSize: "14px" }}>
                      {availableCredits < DAYS_PER_YEAR
                        ? `Onboarding a customer? Grab a 1-year pack now and pay within 30 days.`
                        : `Bigger packs cost less per credit — from ${money(profitPerYearLicense(CREDIT_PACKS[0]))} profit per license on 1 Year to ${money(profitPerYearLicense(bestMarginPack()))} on ${bestMarginPack().label}.`}
                    </div>
                  </div>
                </div>
                <Link to="/Admin/BuyCredits" className="btn admin-btn">Buy credits</Link>
              </div>
            )}

            {/* Credit History */}
            <Card>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Credit History</h5>
                  <span className="text-muted" style={{ fontSize: "14px" }}>
                    {txTotal} transaction{txTotal !== 1 ? "s" : ""}
                  </span>
                </div>

                {transactions.length === 0 ? (
                  <div className="text-center py-5">
                    <FaHistory 
                      style={{ 
                        fontSize: "48px", 
                        color: "#d1d5db", 
                        marginBottom: "16px" 
                      }} 
                    />
                    <p className="text-muted" style={{ fontSize: "16px", fontWeight: 500 }}>
                      No credit transactions yet.
                    </p>
                    <p className="text-muted mx-auto" style={{ fontSize: "13px", maxWidth: "300px" }}>
                      When you assign credits to users or receive new credits, they will appear here.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table hover size="sm">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Type</th>
                            <th>Credits</th>
                            <th>User</th>
                            <th>Notes</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map((tx, i) => (
                            <tr key={tx.id}>
                              <td>{txTotal - ((txPage - 1) * TX_LIMIT + i)}</td>
                              {/* <td>{(txPage - 1) * TX_LIMIT + i + 1}</td> */}
                              <td>
                                <span style={{
                                  display: "inline-block",
                                  padding: "2px 10px",
                                  borderRadius: "10px",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  backgroundColor: tx.transaction_type === "assigned" ? "#e6f7ef" : "#fff0f0",
                                  color: tx.transaction_type === "assigned" ? "#016641" : "#dc3545",
                                }}>
                                  {tx.transaction_type === "assigned" ? "+ Assigned" : "− Used"}
                                </span>
                              </td>
                              <td style={{ fontWeight: 600 }}>{tx.credits.toLocaleString()}</td>
                              <td style={{ fontSize: "13px" }}>{tx.target_user_email || "—"}</td>
                              <td className="text-muted" style={{ fontSize: "13px" }}>{tx.notes || "—"}</td>
                              <td style={{ fontSize: "13px", whiteSpace: "nowrap" }}>{ShowTime(tx.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <span className="text-muted" style={{ fontSize: "13px" }}>
                          Page {txPage} of {totalPages}
                        </span>
                        <div className="d-flex gap-2">
                          <button
                            style={pagerBtnStyle(txPage === 1)}
                            disabled={txPage === 1}
                            onClick={() => setTxPage(p => p - 1)}
                          >
                            ← Prev
                          </button>
                          <button
                            style={pagerBtnStyle(txPage >= totalPages)}
                            disabled={txPage >= totalPages}
                            onClick={() => setTxPage(p => p + 1)}
                          >
                            Next →
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>

          </div>
        </div>
      </div>
    </div>
  );
}
